/**
 * Product Gallery Client Tags
 * Handles client measurement tags interaction on product gallery images
 * 
 * Features:
 * - Mobile expand/collapse functionality
 * - Unit conversion (CM/Inches)
 * - Responsive behavior
 */

import { CLASS_VISIBLE } from '../tools/constants/classes';
import { responsiveMediaAbove, responsiveMediaBelow } from '../settings/responsive';

const SELECTOR_CLIENT_TAG_VALUE = '[data-client-tag-value]';

export class ProductGalleryClientTags {
  private gallery: HTMLElement;
  private seeDetailsButtons: NodeListOf<HTMLButtonElement>;
  private hideDetailsButtons: NodeListOf<HTMLButtonElement>;
  private modelDetails: NodeListOf<HTMLElement>;

  constructor(gallery: HTMLElement) {
    this.gallery = gallery;
    this.seeDetailsButtons = this.gallery.querySelectorAll('[data-see-details]');
    this.hideDetailsButtons = this.gallery.querySelectorAll('[data-hide-details]');
    this.modelDetails = this.gallery.querySelectorAll('[data-model-details]');
    
    this.init();
  }

  private init(): void {
    this.bindEvents();
  }

  private bindEvents(): void {
    // Handle "See Model Details" button clicks (with touch support)
    this.seeDetailsButtons.forEach((button) => {
      // Add both click and touchend for better mobile support
      ['click', 'touchend'].forEach(eventType => {
        button.addEventListener(eventType, (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const tagIndex = button.dataset.seeDetails;
          this.showModelDetails(tagIndex);
          
          // Reset opacity after action to prevent stuck opacity
          button.style.opacity = '';
        });
      });

      // Add visual feedback for touch - without preventDefault to allow click events
      button.addEventListener('touchstart', () => {
        button.style.opacity = '0.6';
      });

      button.addEventListener('touchcancel', () => {
        button.style.opacity = '';
      });
    });

    // Handle "Hide Model Details" button clicks (with touch support)
    this.hideDetailsButtons.forEach((button) => {
      // Add both click and touchend for better mobile support
      ['click', 'touchend'].forEach(eventType => {
        button.addEventListener(eventType, (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          const tagIndex = button.dataset.hideDetails;
          this.hideModelDetails(tagIndex);
          
          // Reset opacity after action to prevent stuck opacity
          button.style.opacity = '';
        });
      });

      // Add visual feedback for touch - without preventDefault to allow click events
      button.addEventListener('touchstart', () => {
        button.style.opacity = '0.6';
      });

      button.addEventListener('touchcancel', () => {
        button.style.opacity = '';
      });
    });

    // Close all expanded details when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.gallery.contains(e.target as Node)) {
        this.hideAllDetails();
      }
    });

    // Hide all details on resize to desktop
    let resizeTimeout: NodeJS.Timeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (responsiveMediaAbove('medium')) { // Use the exact breakpoint
          this.hideAllDetails();
        }
      }, 250); // Debounce resize events for better performance
    });
  }

  private showModelDetails(tagIndex: string | undefined): void {
    if (!tagIndex) return;

    // Hide all other details first
    this.hideAllDetails();

    // Show the selected model details
    const modelDetail = this.gallery.querySelector(`[data-model-details="${tagIndex}"]`) as HTMLElement;
    const seeButton = this.gallery.querySelector(`[data-see-details="${tagIndex}"]`) as HTMLButtonElement;
    const hideButton = this.gallery.querySelector(`[data-hide-details="${tagIndex}"]`) as HTMLButtonElement;
    
    // Find the client tags container for this specific tag
    const clientTagsContainer = this.gallery.querySelector(`[data-client-tags="${tagIndex}"]`) as HTMLElement;

    if (modelDetail && seeButton && hideButton && clientTagsContainer) {
      // Measure the current width (collapsed state) before expanding
      const collapsedWidth = clientTagsContainer.offsetWidth;
      
      // Set this width as minimum to prevent width jumping during toggle
      // Only apply on mobile/tablet screens where expansion happens
      if (responsiveMediaBelow('medium')) {
        clientTagsContainer.style.minWidth = `${collapsedWidth}px`;
      }
      
      // Add a small delay to ensure smooth animation
      requestAnimationFrame(() => {
        modelDetail.classList.add(CLASS_VISIBLE);
        seeButton.style.display = 'none';
        hideButton.classList.add(CLASS_VISIBLE);
      });
    }
  }

  private hideModelDetails(tagIndex: string | undefined): void {
    if (!tagIndex) return;

    const modelDetail = this.gallery.querySelector(`[data-model-details="${tagIndex}"]`) as HTMLElement;
    const seeButton = this.gallery.querySelector(`[data-see-details="${tagIndex}"]`) as HTMLButtonElement;
    const hideButton = this.gallery.querySelector(`[data-hide-details="${tagIndex}"]`) as HTMLButtonElement;
    
    // Find the client tags container for this specific tag
    const clientTagsContainer = this.gallery.querySelector(`[data-client-tags="${tagIndex}"]`) as HTMLElement;

    if (modelDetail && seeButton && hideButton) {
      modelDetail.classList.remove(CLASS_VISIBLE);
      seeButton.style.display = 'flex';
      hideButton.classList.remove(CLASS_VISIBLE);
      
      // Reset min-width to allow natural sizing after collapse
      if (clientTagsContainer) {
        clientTagsContainer.style.minWidth = '';
      }
    }
  }

  private hideAllDetails(): void {
    this.modelDetails.forEach((detail) => {
      detail.classList.remove(CLASS_VISIBLE);
    });

    this.seeDetailsButtons.forEach((button) => {
      button.style.display = 'flex';
    });

    this.hideDetailsButtons.forEach((button) => {
      button.classList.remove(CLASS_VISIBLE);
    });
    
    // Reset min-width for all client tags containers
    const allClientTagsContainers = this.gallery.querySelectorAll('[data-client-tags]') as NodeListOf<HTMLElement>;
    allClientTagsContainers.forEach((container) => {
      container.style.minWidth = '';
    });
  }

  // Unit conversion functionality (for future implementation)
  public convertUnits(isInches: boolean): void {
    const blocks = Array.from(this.gallery.querySelectorAll('[data-client-tags]'));

    blocks.forEach((block) => {
      const valueElements = Array.from(block.querySelectorAll(SELECTOR_CLIENT_TAG_VALUE));

      valueElements.forEach((element) => {
        const raw = element.textContent || '';
        const currentText = raw.trim();
        const cmMatch = currentText.match(/(\d+\.?\d*)\s*CM/i);
        const inMatch = currentText.match(/(\d+\.?\d*)\s*"/);

        if (isInches && cmMatch) {
          const numericValue = parseFloat(cmMatch[1]);
          const inches = Math.round((numericValue / 2.54) * 10) / 10;
          element.textContent = currentText.replace(/(\d+\.?\d*)\s*CM/i, `${inches}"`);
        } else if (!isInches && inMatch) {
          const numericValue = parseFloat(inMatch[1]);
          const cm = Math.round(numericValue * 2.54);
          element.textContent = currentText.replace(/(\d+\.?\d*)\s*"/, `${cm}CM`);
        }
      });
    });
  }

  // Public method to destroy the instance
  public destroy(): void {
    this.seeDetailsButtons.forEach((button) => {
      button.removeEventListener('click', this.showModelDetails.bind(this));
    });

    this.hideDetailsButtons.forEach((button) => {
      button.removeEventListener('click', this.hideModelDetails.bind(this));
    });
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const galleries = document.querySelectorAll('[data-product-gallery]') as NodeListOf<HTMLElement>;
  
  galleries.forEach((gallery) => {
    new ProductGalleryClientTags(gallery);
  });
});
